import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import StatsGrid from '../components/StatsGrid';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getDashboardStats } from '../lib/admin-api';
import { DashboardStats } from '../types/admin';
import { Loader2, ArrowLeft } from 'lucide-react';
import WordUsageTable from '../components/WordUsageTable';
import { Link } from 'react-router-dom';

import { useToast } from '@/hooks/use-sonner-toast';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const dashboardStats = await getDashboardStats();
        setStats(dashboardStats);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        toast({
          title: 'Error',
          description: 'Failed to load dashboard statistics',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [toast]);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Failed to load dashboard data</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        <StatsGrid
          stats={{
            totalUsers: stats.userStats.totalUsers,
            activeUsers: stats.userStats.activeUsers,
            newUsers: stats.userStats.newUsers,
            premiumUsers: stats.userStats.premiumUsers,
            totalReports: stats.totalReports,
          }}
        />

        <div className="grid grid-cols-1 gap-6">
          <Card className="border-border shadow-sm overflow-hidden">
            <CardHeader>
              <CardTitle>Word Usage</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="all" className="px-4 pt-2">
                <TabsList className="mb-4">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="item">Item</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="character">Character</TabsTrigger>
                </TabsList>
                <TabsContent value="all">
                  <WordUsageTable
                    words={stats.mostUsedWords.slice(0, 10)}
                    title="Top 10 Most Used Words"
                  />
                </TabsContent>
                <TabsContent value="item">
                  <WordUsageTable
                    words={stats.mostUsedWords
                      .filter((word) => word.forgeTypes.includes('Item'))
                      .slice(0, 10)}
                    title="Top 10 Most Used Item Words"
                  />
                </TabsContent>
                <TabsContent value="location">
                  <WordUsageTable
                    words={stats.mostUsedWords
                      .filter((word) => word.forgeTypes.includes('Location'))
                      .slice(0, 10)}
                    title="Top 10 Most Used Location Words"
                  />
                </TabsContent>
                <TabsContent value="character">
                  <WordUsageTable
                    words={stats.mostUsedWords
                      .filter((word) => word.forgeTypes.includes('Character'))
                      .slice(0, 10)}
                    title="Top 10 Most Used Character Words"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
